import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { resendFirmaRemotaHT, sendPdfEmailHT } from '../../api/common';
import { Alertassw } from '../Alertas/Alertassw';

/**
 * Modal para confirmar/editar email antes de enviar
 * Usado para:
 * - Reenviar solicitud de firma remota (action='resend')
 * - Enviar PDF por correo (action='send')
 */
const ModalConfirmarEmailHT = ({ show, onHide, ht, action, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [emailOriginal, setEmailOriginal] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cargar email al abrir modal
  useEffect(() => {
    if (show && ht) {
      let emailInicial = '';
      
      if (action === 'resend') {
        // Para reenvío de firma, usar el email guardado o cliente.correo como fallback
        emailInicial = ht.firmaClienteEmail || ht.cliente?.correo || '';
      } else if (action === 'send') {
        // Para envío de PDF, usar cliente.correo
        emailInicial = ht.cliente?.correo || '';
      }
      
      setEmail(emailInicial);
      setEmailOriginal(emailInicial);
      setError('');
    }
  }, [show, ht, action]);

  // Validar formato de email
  const validarEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const emailCambio = email !== emailOriginal;
  const emailValido = validarEmail(email);

  const handleSubmit = async () => {
    if (!emailValido) {
      setError('Por favor ingrese un email válido');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (action === 'resend') {
        // Reenviar solicitud de firma
        console.log('🔄 [ModalConfirmarEmail] Reenviando firma a:', email);
        console.log('📧 [ModalConfirmarEmail] Email cambió:', emailCambio ? 'Sí' : 'No');
        
        const response = await resendFirmaRemotaHT(ht._id, emailCambio ? email : null);
        
        if (response.status === 200) {
          Alertassw('success', 'Solicitud reenviada', `Se ha enviado el correo a ${email}`);
          
          if (onSuccess) onSuccess();
          onHide();
        } else {
          throw new Error(response.data?.message || 'Error al reenviar');
        }
      } else if (action === 'send') {
        // Enviar PDF por correo
        console.log('📧 [ModalConfirmarEmail] Enviando PDF a:', email);
        
        const response = await sendPdfEmailHT(ht._id, email);
        
        if (response.status === 200) {
          Alertassw('success', 'PDF enviado', `El PDF ha sido enviado a ${email}`);
          
          if (onSuccess) onSuccess();
          onHide();
        } else {
          throw new Error(response.data?.message || 'Error al enviar PDF');
        }
      }
    } catch (error) {
      console.error('❌ [ModalConfirmarEmail] Error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error al procesar solicitud';
      setError(errorMsg);
      Alertassw('error', 'Error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getTitulo = () => {
    if (action === 'resend') return 'Reenviar Solicitud de Firma';
    if (action === 'send') return 'Enviar PDF por Correo';
    return 'Confirmar Email';
  };

  const getMensaje = () => {
    if (action === 'resend') {
      return emailCambio 
        ? 'Se actualizará el email y se reenviará la solicitud de firma'
        : 'Se reenviará la solicitud de firma al mismo correo';
    }
    if (action === 'send') {
      return 'Se enviará el PDF de la Hoja de Trabajo firmada al correo indicado';
    }
    return '';
  };

  const getBotonTexto = () => {
    if (loading) return <><Spinner size="sm" animation="border" className="me-2" />Enviando...</>;
    if (action === 'resend') return emailCambio ? 'Cambiar y Reenviar' : 'Reenviar al mismo';
    if (action === 'send') return 'Enviar PDF';
    return 'Confirmar';
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <i className={`bi ${action === 'resend' ? 'bi-arrow-repeat' : 'bi-envelope'} me-2`}></i>
          {getTitulo()}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        <div className="mb-3">
          <p className="text-muted">
            <strong>Hoja de Trabajo:</strong> {ht?.numeroOrden}-{ht?.visitas}
          </p>
          <p className="text-muted mb-3">{getMensaje()}</p>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">
            <i className="bi bi-envelope me-2"></i>
            Correo electrónico {emailCambio && <Badge bg="warning" text="dark">Modificado</Badge>}
          </Form.Label>
          <Form.Control
            type="email"
            placeholder="ejemplo@correo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            isInvalid={email && !emailValido}
            isValid={email && emailValido}
            disabled={loading}
          />
          <Form.Control.Feedback type="invalid">
            Email inválido
          </Form.Control.Feedback>
          {emailCambio && emailValido && (
            <Form.Text className="text-warning">
              <i className="bi bi-exclamation-circle me-1"></i>
              Se actualizará el email guardado para futuras comunicaciones
            </Form.Text>
          )}
        </Form.Group>

        {action === 'resend' && ht?.firmaClienteTokenExpira && (
          <Alert variant="info" className="small">
            <i className="bi bi-info-circle me-2"></i>
            {new Date(ht.firmaClienteTokenExpira) < new Date() 
              ? '⚠️ El token anterior expiró. Se generará uno nuevo automáticamente.'
              : `✅ El token actual es válido hasta el ${new Date(ht.firmaClienteTokenExpira).toLocaleDateString()}`
            }
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          variant={action === 'resend' ? 'warning' : 'success'}
          onClick={handleSubmit}
          disabled={loading || !emailValido}
        >
          {getBotonTexto()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalConfirmarEmailHT;
